/**
 * Link colours.
 *
 * `link_key` is what travels to the backend on the order item and on every
 * package cut from it, so the keys must stay stable across releases —
 * renaming one orphans the links on every shipment already saved with it.
 *
 * The colour is DERIVED from the key rather than stored alongside it, so the
 * palette can never run out: keys are L1, L2, L3 … with no upper bound and
 * each maps to its own hue. Only lightness and saturation are themed (see
 * --color-link-* in src/theme/tokens.css), which is what keeps a generated
 * hue legible on both light and dark surfaces.
 */

// Consecutive keys land far apart on the wheel, so the first handful of
// products get maximally distinct colours instead of neighbouring shades.
const GOLDEN_ANGLE = 137.508;

// The brand sits at ~212-219deg (--color-brand / --color-brand-text). Hues are
// laid out on a circle with that arc cut out, so a link bar is never mistaken
// for a brand element.
const BAND_START = 198;
const BAND_END = 232;
const BAND_WIDTH = BAND_END - BAND_START;
const USABLE_ARC = 360 - BAND_WIDTH;

/** "L7" -> 7. Anything unrecognised (including "") -> 0, meaning "not linked". */
export const linkKeyIndex = (key) => {
  const match = /^L(\d+)$/.exec(String(key ?? ""));
  const index = match ? parseInt(match[1], 10) : 0;
  return index > 0 ? index : 0;
};

export const linkKeyAt = (slot) => `L${slot}`;

const hueFor = (index) => {
  const raw =
    ((((index - 1) * GOLDEN_ANGLE) % USABLE_ARC) + USABLE_ARC) % USABLE_ARC;
  const hue = raw < BAND_START ? raw : raw + BAND_WIDTH;
  return Math.round(hue * 10) / 10;
};

/** CSS colour for a link key, or null when the row is not linked. */
export const linkColor = (key) => {
  const index = linkKeyIndex(key);
  if (!index) return null;
  return `hsl(${hueFor(index)}, var(--color-link-saturation), var(--color-link-lightness))`;
};

/** Lowest key nobody is using. Unbounded, so it never has to reuse a colour. */
export const nextLinkKey = (usedKeys) => {
  const used = new Set(
    (Array.isArray(usedKeys) ? usedKeys : []).filter(Boolean),
  );
  let slot = 1;
  while (used.has(linkKeyAt(slot))) slot += 1;
  return linkKeyAt(slot);
};

/** { L3: "101-product", … } — what each colour currently stands for. */
export const buildLinkUsage = (products) =>
  (Array.isArray(products) ? products : []).reduce((usage, product) => {
    if (!product?.link_key) return usage;
    const label =
      product.full_name ?? product.name ?? product.code ?? "Product";
    usage[product.link_key] = usage[product.link_key]
      ? `${usage[product.link_key]}, ${label}`
      : label;
    return usage;
  }, {});
